/** Build the board's FS-chain lane map: each in-plan activity's board id → the
 *  board id of the FS predecessor it should share a lane with (the latest-
 *  finishing one). Committed activities are their numeric id; a draft-added
 *  activity is its negative protoNewActs slot (-(index+1)), matching the member
 *  the board renders — so a JUST-ADDED activity chains into its predecessor's
 *  lane instead of starting its own. Ties to activities the board doesn't carry
 *  (externals) are ignored. Pure, so the id translation is unit-tested. */
import type { DepGraph } from "../../../models";

export function buildBoardFsLinks(
  graph: DepGraph | null,
  /** protoNewActs draft ids, in slot order; "" for a tombstoned slot so the
   *  index → negative-id mapping stays stable. */
  draftIdsBySlot: string[],
): Map<number, number> {
  const m = new Map<number, number>();
  if (!graph) return m;

  const negIdByDraft = new Map<string, number>();
  draftIdsBySlot.forEach((id, i) => {
    if (id) negIdByDraft.set(id, -(i + 1));
  });
  const boardId = (id: string): number | null =>
    /^\d+$/.test(id) ? Number(id) : negIdByDraft.get(id) ?? null;

  for (const succId of Object.keys(graph.edges)) {
    const succBoard = boardId(succId);
    if (succBoard == null) continue; // a tie the board doesn't carry
    let best: number | null = null;
    let bestFinish = "";
    for (const e of graph.edges[succId]) {
      if (e.type !== "FS") continue;
      const predBoard = boardId(e.pred_id);
      if (predBoard == null) continue;
      const pred = graph.activities[e.pred_id];
      const fin = pred?.drive_finish ?? pred?.finish ?? "";
      if (best == null || fin > bestFinish) {
        best = predBoard;
        bestFinish = fin;
      }
    }
    if (best != null) m.set(succBoard, best);
  }
  return m;
}
